import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import RiderLayout from "@/components/RiderLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Camera, MapPin, LogIn, LogOut, CheckCircle, Clock, 
  RotateCcw, Loader2, Calendar, Gauge, Coffee, Activity, X
} from "lucide-react";

const RiderAttendance = ({ user, onLogout }) => {
  const selfieVideoRef = useRef(null);
  const selfieCanvasRef = useRef(null);
  const odometerVideoRef = useRef(null);
  const odometerCanvasRef = useRef(null);
  
  const [todayData, setTodayData] = useState({ activities: [], is_checked_in: false });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Camera states
  const [showSelfieCamera, setShowSelfieCamera] = useState(false);
  const [selfieImage, setSelfieImage] = useState(null);
  const [selfieStream, setSelfieStream] = useState(null);
  
  const [showOdometerCamera, setShowOdometerCamera] = useState(false);
  const [odometerImage, setOdometerImage] = useState(null);
  const [odometerStream, setOdometerStream] = useState(null);
  
  // Form states
  const [bikeKm, setBikeKm] = useState("");
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);

  const [attendanceHistory, setAttendanceHistory] = useState([]);

  useEffect(() => {
    fetchTodayData();
    fetchAttendanceHistory();
    
    return () => {
      // Cleanup camera streams
      stopCamera(selfieStream, setSelfieStream, setShowSelfieCamera);
      stopCamera(odometerStream, setOdometerStream, setShowOdometerCamera);
    };
  }, []);

  const fetchTodayData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/rider/activity/today`);
      setTodayData(res.data);
    } catch (e) {
      console.error("Failed to fetch today's activities:", e);
      setTodayData({ activities: [], is_checked_in: false });
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceHistory = async () => {
    try {
      const res = await axios.get(`${API}/rider/attendance/history?limit=7`);
      setAttendanceHistory(res.data.data || []);
    } catch (e) {
      // Silent
    }
  };

  const startCamera = async (videoRef, setStream, setShowCamera, facingMode = "user") => {
    try {
      // Request camera with specific constraints
      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 640 },
          height: { ideal: 480 }
        },
        audio: false
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(stream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // Ensure video plays
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play().catch(e => console.log("Video play error:", e));
        };
      }
      setShowCamera(true);
      
      // Get location when starting selfie camera
      if (facingMode === "user") {
        getCurrentLocation();
      }
      
      return true;
    } catch (error) {
      console.error("Camera error:", error);
      toast.error(`Camera access failed: ${error.message}`);
      return false;
    }
  };

  const stopCamera = (stream, setStream, setShowCamera) => {
    if (stream) {
      stream.getTracks().forEach(track => {
        track.stop();
      });
      setStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = (videoRef, canvasRef, setImage, stream, setStream, setShowCamera) => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      // Set canvas dimensions to match video
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;
      
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Get base64 image
      const imageData = canvas.toDataURL("image/jpeg", 0.7);
      setImage(imageData);
      
      // Stop camera
      stopCamera(stream, setStream, setShowCamera);
    }
  };

  const getCurrentLocation = () => {
    setGettingLocation(true);
    setLocationError(null);
    
    if (!navigator.geolocation) {
      setLocationError("Geolocation not supported");
      setGettingLocation(false);
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
        setGettingLocation(false);
        toast.success("Location captured!");
      },
      (error) => {
        setLocationError(error.message);
        setGettingLocation(false);
        toast.error("Location access failed. Please enable GPS.");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const recordActivity = async (activityType) => {
    // Validate required fields
    if (!selfieImage) {
      toast.error("Please take a selfie");
      return;
    }
    if (!bikeKm || isNaN(parseFloat(bikeKm))) {
      toast.error("Please enter valid bike KM reading");
      return;
    }
    if (!currentLocation) {
      toast.error("Location is required. Please enable GPS.");
      getCurrentLocation();
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        activity_type: activityType,
        selfie_image: selfieImage,
        odometer_image: odometerImage || null,
        odometer_reading: parseFloat(bikeKm),
        location: currentLocation
      };
      
      const res = await axios.post(`${API}/rider/activity`, payload);
      
      toast.success(`${activityType.replace("_", " ")} recorded successfully!`);
      
      // Reset form
      setSelfieImage(null);
      setOdometerImage(null);
      setBikeKm("");
      
      // Refresh data
      fetchTodayData();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || `${activityType} failed`;
      toast.error(errorMsg);
      console.error("Activity error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
    } catch {
      return iso;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    } catch {
      return dateStr;
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case "CHECK_IN": return <LogIn className="w-4 h-4 text-green-600" />;
      case "CHECK_OUT": return <LogOut className="w-4 h-4 text-red-600" />;
      case "LOGIN": return <Activity className="w-4 h-4 text-blue-600" />;
      case "LOGOUT": return <Activity className="w-4 h-4 text-slate-600" />;
      case "BREAK_START": return <Coffee className="w-4 h-4 text-amber-600" />;
      case "BREAK_END": return <Coffee className="w-4 h-4 text-green-600" />;
      default: return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case "CHECK_IN": return "bg-green-100 text-green-700";
      case "CHECK_OUT": return "bg-red-100 text-red-700";
      case "LOGIN": return "bg-blue-100 text-blue-700";
      case "LOGOUT": return "bg-slate-100 text-slate-700";
      case "BREAK_START": return "bg-amber-100 text-amber-700";
      case "BREAK_END": return "bg-green-100 text-green-700";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  const isCheckedIn = todayData.is_checked_in;

  if (loading) {
    return (
      <RiderLayout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      </RiderLayout>
    );
  }

  return (
    <RiderLayout user={user} onLogout={onLogout}>
      <div className="space-y-4" data-testid="rider-attendance">
        <h1 className="text-2xl font-bold text-slate-800">Attendance</h1>

        {/* Today's Status */}
        <Card className={`border-0 shadow-md ${
          isCheckedIn ? "bg-emerald-50 border-l-4 border-l-emerald-500" : "bg-orange-50 border-l-4 border-l-orange-500"
        }`}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Current Status</p>
                {isCheckedIn ? (
                  <Badge className="bg-emerald-100 text-emerald-700">
                    <CheckCircle className="w-3.5 h-3.5 mr-1" /> On Duty
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-orange-600 border-orange-300">
                    <Clock className="w-3.5 h-3.5 mr-1" /> Off Duty
                  </Badge>
                )}
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">
                  {todayData.activities.length} activities today
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Today's Activity Timeline */}
        {todayData.activities.length > 0 && (
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-500" />
                Today's Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {todayData.activities.map((activity, idx) => (
                  <div key={activity._id || idx} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                    {getActivityIcon(activity.activity_type)}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge className={`text-xs ${getActivityColor(activity.activity_type)}`}>
                          {activity.activity_type.replace("_", " ")}
                        </Badge>
                        <span className="text-xs text-slate-500">{formatTime(activity.timestamp)}</span>
                      </div>
                      {activity.odometer_reading && (
                        <p className="text-xs text-slate-400 mt-0.5">
                          Odometer: {activity.odometer_reading} km
                        </p>
                      )}
                      {activity.selfie_url && (
                        <a 
                          href={activity.selfie_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs text-blue-500 hover:underline"
                        >
                          View Selfie
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Check In / Check Out Form */}
        <Card className="border-0 shadow-lg">
          <CardContent className="pt-5">
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              {isCheckedIn ? (
                <><LogOut className="w-5 h-5 text-red-500" /> Check Out</>
              ) : (
                <><LogIn className="w-5 h-5 text-emerald-500" /> Check In</>
              )}
            </h2>

            {/* Selfie Section */}
            <div className="mb-4">
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                <Camera className="w-4 h-4 inline mr-1" />
                Selfie <span className="text-red-500">*</span>
              </Label>
              
              {showSelfieCamera ? (
                <div className="relative rounded-lg overflow-hidden bg-black">
                  <video 
                    ref={selfieVideoRef} 
                    autoPlay 
                    playsInline
                    muted
                    className="w-full aspect-[4/3] object-cover"
                  />
                  <canvas ref={selfieCanvasRef} className="hidden" />
                  <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-3">
                    <Button 
                      onClick={() => capturePhoto(
                        selfieVideoRef, selfieCanvasRef, setSelfieImage,
                        selfieStream, setSelfieStream, setShowSelfieCamera
                      )}
                      className="bg-white text-slate-800 hover:bg-slate-100 rounded-full w-14 h-14"
                      data-testid="capture-selfie-btn"
                    >
                      <Camera className="w-7 h-7" />
                    </Button>
                    <Button
                      onClick={() => stopCamera(selfieStream, setSelfieStream, setShowSelfieCamera)}
                      variant="destructive"
                      size="icon"
                      className="rounded-full"
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              ) : selfieImage ? (
                <div className="relative rounded-lg overflow-hidden">
                  <img src={selfieImage} alt="Selfie" className="w-full aspect-[4/3] object-cover" />
                  <Badge className="absolute top-2 left-2 bg-emerald-500">
                    <CheckCircle className="w-3.5 h-3.5 mr-1" /> Captured
                  </Badge>
                  <Button 
                    onClick={() => {
                      setSelfieImage(null);
                      startCamera(selfieVideoRef, setSelfieStream, setShowSelfieCamera, "user");
                    }}
                    variant="outline"
                    size="sm"
                    className="absolute bottom-2 right-2 bg-white"
                    data-testid="retake-selfie-btn"
                  >
                    <RotateCcw className="w-4 h-4 mr-1" /> Retake
                  </Button>
                </div>
              ) : (
                <Button 
                  onClick={() => startCamera(selfieVideoRef, setSelfieStream, setShowSelfieCamera, "user")}
                  variant="outline"
                  className="w-full h-32 border-dashed border-2 flex flex-col items-center justify-center"
                  data-testid="open-selfie-camera-btn"
                >
                  <Camera className="w-8 h-8 mb-2 text-slate-400" />
                  <span className="text-slate-600">Tap to take selfie</span>
                </Button>
              )}
            </div>

            {/* Odometer Image Section */}
            <div className="mb-4">
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                <Gauge className="w-4 h-4 inline mr-1" />
                Odometer Photo <span className="text-slate-400">(Optional)</span>
              </Label>
              
              {showOdometerCamera ? (
                <div className="relative rounded-lg overflow-hidden bg-black">
                  <video 
                    ref={odometerVideoRef} 
                    autoPlay 
                    playsInline
                    muted
                    className="w-full aspect-[4/3] object-cover"
                  />
                  <canvas ref={odometerCanvasRef} className="hidden" />
                  <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-3">
                    <Button 
                      onClick={() => capturePhoto(
                        odometerVideoRef, odometerCanvasRef, setOdometerImage,
                        odometerStream, setOdometerStream, setShowOdometerCamera
                      )}
                      className="bg-white text-slate-800 hover:bg-slate-100 rounded-full w-14 h-14"
                      data-testid="capture-odometer-btn"
                    >
                      <Camera className="w-7 h-7" />
                    </Button>
                    <Button
                      onClick={() => stopCamera(odometerStream, setOdometerStream, setShowOdometerCamera)}
                      variant="destructive"
                      size="icon"
                      className="rounded-full"
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              ) : odometerImage ? (
                <div className="relative rounded-lg overflow-hidden">
                  <img src={odometerImage} alt="Odometer" className="w-full aspect-[4/3] object-cover" />
                  <Badge className="absolute top-2 left-2 bg-emerald-500">
                    <CheckCircle className="w-3.5 h-3.5 mr-1" /> Captured
                  </Badge>
                  <Button 
                    onClick={() => {
                      setOdometerImage(null);
                      startCamera(odometerVideoRef, setOdometerStream, setShowOdometerCamera, "environment");
                    }}
                    variant="outline"
                    size="sm"
                    className="absolute bottom-2 right-2 bg-white"
                    data-testid="retake-odometer-btn"
                  >
                    <RotateCcw className="w-4 h-4 mr-1" /> Retake
                  </Button>
                </div>
              ) : (
                <Button 
                  onClick={() => startCamera(odometerVideoRef, setOdometerStream, setShowOdometerCamera, "environment")}
                  variant="outline"
                  className="w-full h-24 border-dashed border-2 flex flex-col items-center justify-center"
                  data-testid="open-odometer-camera-btn"
                >
                  <Gauge className="w-6 h-6 mb-1 text-slate-400" />
                  <span className="text-sm text-slate-600">Tap to capture odometer</span>
                </Button>
              )}
            </div>

            {/* Bike KM Input */}
            <div className="mb-4">
              <Label htmlFor="bike-km" className="text-sm font-medium text-slate-700 mb-2 block">
                <Gauge className="w-4 h-4 inline mr-1" />
                Odometer Reading (KM) <span className="text-red-500">*</span>
              </Label>
              <Input
                id="bike-km"
                type="number"
                placeholder="Enter current KM reading"
                value={bikeKm}
                onChange={(e) => setBikeKm(e.target.value)}
                className="text-lg"
                data-testid="bike-km-input"
              />
              {todayData.last_check_in?.odometer_reading && (
                <p className="text-xs text-slate-500 mt-1">
                  Last check-in: {todayData.last_check_in.odometer_reading} km
                </p>
              )}
            </div>

            {/* Location Status */}
            <div className="mb-4 p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className={`w-4 h-4 ${currentLocation ? "text-emerald-600" : "text-slate-400"}`} />
                  <span className="text-sm text-slate-700">Location</span>
                </div>
                {gettingLocation ? (
                  <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                ) : currentLocation ? (
                  <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                    <CheckCircle className="w-3 h-3 mr-1" /> Captured
                  </Badge>
                ) : (
                  <Button variant="ghost" size="sm" onClick={getCurrentLocation}>
                    Get Location
                  </Button>
                )}
              </div>
              {currentLocation && (
                <p className="text-xs text-slate-400 mt-1">
                  Accuracy: {Math.round(currentLocation.accuracy)}m
                </p>
              )}
              {locationError && (
                <p className="text-xs text-red-500 mt-1">{locationError}</p>
              )}
            </div>

            {/* Submit Buttons */}
            <div className="space-y-2">
              <Button 
                onClick={() => recordActivity(isCheckedIn ? "CHECK_OUT" : "CHECK_IN")}
                disabled={submitting || !selfieImage || !bikeKm || !currentLocation}
                className={`w-full py-6 text-base ${isCheckedIn ? "bg-red-500 hover:bg-red-600" : "bg-emerald-500 hover:bg-emerald-600"}`}
                data-testid={isCheckedIn ? "checkout-btn" : "checkin-btn"}
              >
                {submitting ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Uploading...</>
                ) : isCheckedIn ? (
                  <><LogOut className="w-5 h-5 mr-2" /> Check Out</>
                ) : (
                  <><LogIn className="w-5 h-5 mr-2" /> Check In</>
                )}
              </Button>
              
              {/* Break buttons - only show when checked in */}
              {isCheckedIn && (
                <div className="grid grid-cols-2 gap-2">
                  <Button 
                    variant="outline"
                    onClick={() => recordActivity("BREAK_START")}
                    disabled={submitting || !selfieImage || !bikeKm || !currentLocation}
                    className="text-amber-600 border-amber-300 hover:bg-amber-50"
                  >
                    <Coffee className="w-4 h-4 mr-1" /> Start Break
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => recordActivity("BREAK_END")}
                    disabled={submitting || !selfieImage || !bikeKm || !currentLocation}
                    className="text-green-600 border-green-300 hover:bg-green-50"
                  >
                    <Coffee className="w-4 h-4 mr-1" /> End Break
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Attendance History */}
        <div className="mt-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-slate-400" /> Recent History
          </h3>
          {attendanceHistory.length === 0 ? (
            <p className="text-sm text-slate-500">No attendance history</p>
          ) : (
            <div className="space-y-2">
              {attendanceHistory.map((att, idx) => (
                <Card key={att.date || idx} className="border-0 shadow-sm">
                  <CardContent className="py-3 px-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-800">{formatDate(att.date)}</p>
                        <p className="text-xs text-slate-500">
                          {formatTime(att.check_in_time)} - {att.check_out_time ? formatTime(att.check_out_time) : "—"}
                        </p>
                      </div>
                      <div className="text-right">
                        {att.check_out_time ? (
                          <Badge className="bg-blue-100 text-blue-700 text-xs">Complete</Badge>
                        ) : att.check_in_time ? (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">In Progress</Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-500 text-xs">—</Badge>
                        )}
                        {att.distance_km > 0 && (
                          <p className="text-xs text-emerald-600 font-medium mt-1">
                            {att.distance_km} km
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </RiderLayout>
  );
};

export default RiderAttendance;
